import React, { useEffect, useRef, useState } from 'react';
import { Rocket, Lightbulb, Users, Award, Target, TrendingUp } from 'lucide-react';
import { aboutData } from '../data/mock';

const About = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const currentRef = sectionRef.current;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []);

  const features = [
    {
      icon: Rocket,
      title: 'Tecnología de Vanguardia',
      description: 'Implementamos las últimas tecnologías para mantener tu negocio a la vanguardia del mercado digital',
      gradient: 'from-blue-500 to-cyan-400'
    },
    {
      icon: Lightbulb,
      title: 'Desarrollo Innovador',
      description: 'Creamos soluciones de software personalizadas que se adaptan perfectamente a las necesidades de tu empresa',
      gradient: 'from-blue-500 to-cyan-400'
    },
    {
      icon: Users,
      title: 'Metodologías Ágiles',
      description: 'Utilizamos metodologías ágiles para garantizar entregas rápidas y de alta calidad en todos nuestros proyectos',
      gradient: 'from-blue-500 to-cyan-400'
    },
    {
      icon: Award,
      title: 'Proyectos Exitosos',
      description: 'Nuestro historial de proyectos exitosos respalda nuestra experiencia y compromiso con la excelencia',
      gradient: 'from-blue-500 to-cyan-400'
    }
  ];

  const stats = [
    { icon: Target, number: '7+', label: 'Años de Experiencia' },
    { icon: Award, number: '10+', label: 'Proyectos Exitosos' },
    { icon: Users, number: '100%', label: 'Clientes Satisfechos' },
    { icon: TrendingUp, number: '24/7', label: 'Soporte Técnico' }
  ];

  return (
    <section id="about" ref={sectionRef} className="py-20 bg-gradient-to-b from-white to-gray-50 relative overflow-hidden">
      {/* Background Decoration */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-blue-100/30 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-cyan-100/30 rounded-full blur-3xl"></div>

      <div className="container mx-auto px-4 relative z-10">
        <div
          className={`max-w-6xl mx-auto transform transition-all duration-1000 ${
            isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
          }`}
        >
          {/* Header */}
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              <span className="text-gray-900">Sobre </span>
              <span className="bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent">Nosotros</span>
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-blue-600 to-cyan-500 mx-auto mb-8"></div>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed md:text-justify">
              {aboutData.description.split('BEZALEL TECHNOLOGY').map((part, index, arr) => (
                <React.Fragment key={index}>
                  {part}
                  {index < arr.length - 1 && (
                    <span className="font-bold text-gray-900">BEZALEL TECHNOLOGY</span>
                  )}
                </React.Fragment>
              ))}
            </p>
          </div>

          {/* Features Grid - 2x2 */}
          <div className="grid md:grid-cols-2 gap-8 mb-16">
            {features.map((feature, index) => (
              <div
                key={index}
                className={`bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100 group ${
                  isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
                }`}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <div className={`w-16 h-16 bg-gradient-to-br ${feature.gradient} rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                  <feature.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{feature.title}</h3>
                <p className="text-gray-600 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>

          {/* Stats Section */}
          <div className="bg-gradient-to-br from-blue-600 to-cyan-500 rounded-3xl p-10 md:p-16 text-white shadow-2xl relative overflow-hidden">
            {/* Mascot decoration */}
            <div className="absolute -bottom-10 -right-10 opacity-10">
              <img
                src="https://customer-assets.emergentagent.com/job_bezalel-ux-update/artifacts/k54w94pu_bezalel.png"
                alt="Bezalel Mascot"
                className="w-80 h-auto"
              />
            </div>
            
            <div className="relative z-10">
              <h3 className="text-3xl md:text-4xl font-bold mb-4 text-center">
                ¿Por qué Elegirnos?
              </h3>
              <p className="text-xl text-center text-cyan-50 mb-12 max-w-2xl mx-auto">
                Somos tu aliado estratégico en transformación digital
              </p>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                {stats.map((stat, index) => (
                  <div
                    key={index}
                    className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 text-center border border-white/20 hover:bg-white/20 transition-all duration-300 transform hover:scale-105"
                  >
                    <div className="flex justify-center mb-3">
                      <stat.icon className="w-8 h-8 text-cyan-200" />
                    </div>
                    <div className="text-4xl font-bold mb-2">{stat.number}</div>
                    <div className="text-sm text-cyan-100">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
